import { Component, Input, ViewChild } from '@angular/core';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { ChangeHistoryService } from '../../../services/change-history/change-history-service';
import {
  ChangeHistoryDetailsObject,
  ChangeHistoryFilters,
  ChangeEntry,
  ChangeHistoryRecordFilters,
  ChangeHistorySummary
} from '../../../models/change-history/change-history.models';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { columns } from './change-history.config';
import { process, State } from '@progress/kendo-data-query';
import { AppConfig } from '../../../app/app.config';
import { ImplementerSnapshotSummary } from '../../../models/implementer/implementer.models';
import { ImplementerService } from '../../../services/implementer/implementer.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';

@Component({
  selector: 'change-history-component',
  templateUrl: 'change-history-component.html'
})
export class ChangeHistoryComponent {
  @Input() startDailyLogDate: PtLuxonDate;
  @Input() creationTimeFilterDate: PtLuxonDate;
  @Input() snapshotSummaryId: number;
  @ViewChild('changeHistoryGrid') grid?: GridComponent;

  config = AppConfig.settings;

  text: string;
  changeHistorySummaries: ChangeHistorySummary[];
  chosenSnapshot: ImplementerSnapshotSummary;
  chosenSnapshotEntries: ChangeEntry[];
  gridData: GridDataResult;
  columns = columns;
  loading: boolean;
  tabDataLoading: boolean;
  activeChangeHistoryTabId: number;

  state: State = {
    take: 500,
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      {
        field: 'timeOfChange',
        dir: 'desc'
      },
      {
        field: 'id',
        dir: 'desc'
      }
    ],
  };

  constructor(
    private changeHistoryService: ChangeHistoryService,
    private implementerService: ImplementerService,
    private dateParserService: DateParserService
  ) {
  }

  async ngOnInit() {
    this.loading = true;
    await this.loadChangeHistory();
  }

  async loadChangeHistory() {
    const filters = await this.createChangeHistoryFilters();

    this.changeHistorySummaries = await this.changeHistoryService.getChangeHistoryForDate(filters);

    if (this.changeHistorySummaries && this.changeHistorySummaries.length > 0) {
      this.activeChangeHistoryTabId = this.changeHistorySummaries[0].id;
      this.chosenSnapshot = this.changeHistorySummaries[0].implementerSnapshotSummary;
    } else {
      this.activeChangeHistoryTabId = 0;
    }

    // if implementer env, then first tab will always be the current active changes being made, which we dont load in the same way as previous changes
    // else we need to hit the api to load the records for the first tab (as this normally happens on user clicking the tab)
    if (this.config.isImplementerEnvironment) {
      await this.loadCurrentImplementerRecords();
    } else if (this.changeHistorySummaries && this.changeHistorySummaries.length > 0) {
      await this.loadRecordsForChosenSnapshot(this.changeHistorySummaries[0].id);
    }

    this.loading = false;
  }

  async createChangeHistoryFilters(): Promise<ChangeHistoryFilters> {
    if (!this.snapshotSummaryId) {
      const activeSummary = await this.implementerService.getActiveSnapshotSummary(this.config.isVesselEnvironment);

      if (activeSummary) {
        this.snapshotSummaryId = activeSummary.id;
        this.creationTimeFilterDate = activeSummary.creationTime;
      }
    }

    const recordFilters: ChangeHistoryRecordFilters = {
      startDailyLogDate: this.startDailyLogDate.dateTime,
      creationTimeFilterDate: this.creationTimeFilterDate ? this.creationTimeFilterDate.dateTime : null,
    };
    const filters: ChangeHistoryFilters = {
      dailyLogDate: this.startDailyLogDate.dateTime,
      snapshotSummaryId: this.snapshotSummaryId,
      recordFilters: recordFilters
    };

    return filters;
  }

  // Because we return all of the ChangeEntries with no paging, we can apply filters here in the front end instead of having
  // to hit the api and query the DB again. This will have to be changed if we need to implement paging later on.
  async filterChangeEntries(event: any) {
    this.state.filter = event;
    this.gridData = process(this.chosenSnapshotEntries, this.state);
  }

  // TODO - if SJ ask for a filter on the Date column then this will be needed, as the dates aren't deserialised on component load
  // i dont know if this is still true ^^^, after doing the date refactor
  async deSerialiseChangeEntryDates() {
    for (let i = 0; i < this.chosenSnapshotEntries.length; i++) {
      this.chosenSnapshotEntries[i].timeOfChange = this.dateParserService.removeOffsetFromLuxonDate(this.chosenSnapshotEntries[i].timeOfChange);
    }
  }

  // when user clicks on a tab we need to hit the api and fetch the list of records.
  // This has to be done on demand rather than all on page load, as it requires json deserialization on the API.
  async loadRecordsForChosenSnapshot(changeHistorySummaryId: number) {
    this.tabDataLoading = true;

    if (changeHistorySummaryId && changeHistorySummaryId !== 0) {
      this.chosenSnapshotEntries = await this.changeHistoryService.getDeserializedRecords(changeHistorySummaryId)
      this.gridData = process(this.chosenSnapshotEntries, this.state);
    } else {
      await this.loadCurrentImplementerRecords();
    }

    this.tabDataLoading = false;
  }

  async loadCurrentImplementerRecords() {
    if (this.changeHistorySummaries && this.changeHistorySummaries.length > 0) {
      this.chosenSnapshotEntries = this.changeHistorySummaries[0].changeEntries;
      this.gridData = process(this.chosenSnapshotEntries, this.state);
    }
  }

  async onDetailExpand(event: ChangeHistoryDetailsObject) {
    if (!event.dataItem.properties || event.dataItem.properties.length === 0) {
      event.dataItem.loadingProperties = true;
      event.dataItem.properties = await this.changeHistoryService.getPropertiesForEvent(event.dataItem);
      event.dataItem.loadingProperties = false;
    }
  }

  async onTabClick(changeHistorySummaryId: number) {
    this.activeChangeHistoryTabId = changeHistorySummaryId;
    this.chosenSnapshot = this.changeHistorySummaries.find(e => e.id == changeHistorySummaryId).implementerSnapshotSummary;
    this.state.filter.filters = [];

    await this.loadRecordsForChosenSnapshot(changeHistorySummaryId);
  }
}



// WEBPACK FOOTER //
// ./src/pages/change-history/change-history-component/change-history-component.ts